import React from 'react';
import { useTranslation } from 'react-i18next';
import { Text, Button, TextField, themeVars, AlertInline } from '@eds-web/react';

import { useGuardianInfoForm } from '../../hooks/useGuardianInfoForm';
import { StyledGuardianInfo } from './GuardianInfoForm.style';
import { Loading } from '../common/Loading';

interface GuardianInfoFormProps {
    guardianEmail?: string;
}

export const GuardianInfoForm: React.FC<GuardianInfoFormProps> = ({ guardianEmail }) => {
    const { t } = useTranslation();
    const {
        formik,
        handleChangeFormValues,
        updating,
        emailExisted,
        checkingEmail,
        duplicatedEmail
    } = useGuardianInfoForm({ guardianEmail });
    if (checkingEmail) {
        return (
            <div style={{ margin: '64px 0' }}>
                <Loading />
            </div>
        );
    }
    const duplicatedEmailMessage = t(
        'errors.com.epicgames.ecommerce.payout.payoutaccount.duplicated'
    );
    return (
        <form onSubmit={formik.handleSubmit} noValidate>
            <StyledGuardianInfo>
                <Text variant="headingMD">{t('epic.esport.guardian.title.v2')}</Text>

                <div style={{ flexDirection: 'column' }} className="form-column">
                    {emailExisted && (
                        <AlertInline
                            status={'warning'}
                            message={
                                <Text
                                    variant="paragraphMD"
                                    dangerouslySetInnerHTML={{
                                        __html: t('epic.esport.guardianInfo.emailExisted', {
                                            email: guardianEmail
                                        })
                                    }}
                                />
                            }
                        />
                    )}

                    <TextField
                        label={t('epic.esport.label.profile.guardian.email')}
                        name="guardianEmail"
                        type="email"
                        value={formik.values.guardianEmail}
                        onChange={handleChangeFormValues}
                        placeholder={t('epic.esport.label.profile.guardian.email')}
                        disabled={
                            updating || checkingEmail || Boolean(!emailExisted && guardianEmail)
                        }
                        error={
                            formik.touched.guardianEmail &&
                            formik.errors.guardianEmail && (
                                <Text
                                    variant={'uiSM'}
                                    style={{ color: themeVars.color.palette.red['400'] }}
                                    dangerouslySetInnerHTML={{
                                        __html: formik.errors.guardianEmail as string
                                    }}
                                />
                            )
                        }
                        required
                    />

                    {duplicatedEmail && (
                        <AlertInline status={'critical'} message={duplicatedEmailMessage} />
                    )}
                </div>

                <div className="form-column">
                    <TextField
                        label={t('epic.esport.label.profile.guardian.firstName')}
                        name="guardianFirstName"
                        value={formik.values.guardianFirstName}
                        onChange={handleChangeFormValues}
                        placeholder={t('epic.esport.label.profile.guardian.firstName')}
                        disabled={updating}
                        error={formik.touched.guardianFirstName && formik.errors.guardianFirstName}
                        required
                    />

                    <TextField
                        label={t('epic.esport.label.profile.guardian.lastName')}
                        name="guardianLastName"
                        value={formik.values.guardianLastName}
                        onChange={handleChangeFormValues}
                        placeholder={t('epic.esport.label.profile.guardian.lastName')}
                        disabled={updating}
                        error={formik.touched.guardianLastName && formik.errors.guardianLastName}
                        required
                    />
                </div>

                <div className="form-column">
                    <TextField
                        label={t('epic.esport.label.profile.guardian.phone.v2')}
                        name="guardianPhoneNumber"
                        value={formik.values.guardianPhoneNumber}
                        onChange={handleChangeFormValues}
                        placeholder={t('epic.esport.label.profile.guardian.phone.v2')}
                        disabled={updating}
                        error={
                            formik.touched.guardianPhoneNumber && formik.errors.guardianPhoneNumber
                        }
                        required
                    />
                </div>

                <Text
                    variant={'paragraphSM'}
                    style={{ color: themeVars.color.transparent.light['065'] }}
                >
                    {t('epic.esport.guardian.notice')}
                </Text>

                <Button
                    type="submit"
                    variant="cta"
                    size={'xl'}
                    disabled={updating || !formik.dirty || !formik.isValid}
                    loading={updating || checkingEmail}
                    loadingLabel={'loading...'}
                >
                    {t('epic.common.continue')}
                </Button>
            </StyledGuardianInfo>
        </form>
    );
};
